import type { ReactNode } from "react";
import { useOutletContext } from "react-router-dom";
import {
  PlanYearToggle,
  costRange,
  nextYearMarker,
  type M3pProjection,
  type M3pProjectionDrug,
  type Prescription,
} from "@dearbill/shared";
import { HeadlineCards } from "./costEstimate/HeadlineCards";
import type { DashboardOutletContext } from "./MemberDashboard";

/**
 * The Cost Estimate content, rendered inside `MemberLayout` from the dashboard
 * the outlet context carries: the one-fill M3P projection for the selected
 * plan year as headline cards, a basis note while the estimate is priced, and
 * one cost card per prescription on the latest enrollment. The page title is
 * the `<h1>`, the breakdown carries an `<h2>` and each medication card an
 * `<h3>`. The title row holds the shared plan-year toggle after the `<h1>`; it
 * renders nothing while `years.next` is null.
 */
export function CostEstimateScreen(): ReactNode {
  const { data, years, planYear, setPlanYear, m3p } = useOutletContext<DashboardOutletContext>();
  const year = m3p?.contractYear ?? new Date().getFullYear();
  const prescriptions = data.latestEnrollment?.prescriptionsList ?? [];
  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <h1 className="text-h2 text-midnight font-serif">Cost Estimate</h1>
          <PlanYearToggle years={years} value={planYear} onChange={setPlanYear} />
        </div>
        <p className="text-body-sm text-slate-teal">
          {`Your personalized out-of-pocket range, built from the medications on your enrollment and your plan's ${year} cost sharing.`}
        </p>
      </div>
      <HeadlineCards m3p={m3p} />
      {m3p !== null && m3p.totalOop !== null && <BasisNote m3p={m3p} year={year} />}
      <section className="flex flex-col gap-4">
        <h2 className="text-h3 text-midnight font-serif">Estimate Breakdown by Medication</h2>
        {prescriptions.length === 0 ? (
          <p className="text-body-sm text-slate-teal">
            {"Your medications will appear here once they're added to your enrollment."}
          </p>
        ) : (
          prescriptions.map((p) => (
            <DrugCostCard
              key={p.rxcui}
              prescription={p}
              row={m3p?.drugs.find((r) => r.rxcui === p.rxcui)}
            />
          ))
        )}
      </section>
    </>
  );
}

/**
 * The one-fill basis every figure on the page shares. The headline cards'
 * captions explain the two bounds, so the shared range caption is not repeated
 * here. A next-year estimate (`pricesEstimated`) adds the `nextYearMarker`
 * line inside the same note.
 */
function BasisNote({ m3p, year }: { m3p: M3pProjection; year: number }): ReactNode {
  const excluded = m3p.excludedRxcuis.length;
  return (
    <div className="border-haze bg-ice flex flex-col gap-2 rounded-2xl border p-5">
      <p className="text-body-sm text-slate-teal">
        <span className="text-midnight font-semibold">What this estimate means:</span>{" "}
        {`Estimated out-of-pocket cost for one fill of each medication under your plan's ${year} cost sharing, the month you fill all of them. Assumes standard Part D cost sharing; Extra Help members pay less.`}
        {excluded > 0 &&
          ` Excludes ${excluded} ${excluded === 1 ? "medication" : "medications"} not covered by your plan.`}
      </p>
      {m3p.pricesEstimated && (
        <p className="text-body-sm text-slate-teal">{nextYearMarker(year)}</p>
      )}
    </div>
  );
}

interface DrugCostCardProps {
  prescription: Prescription;
  /** The prescription's engine row; absent without a projection or while the plan is unknown. */
  row: M3pProjectionDrug | undefined;
}

/** One medication's card: the name, its label (else the dosage form) and the one-fill "Estimated cost". */
function DrugCostCard({ prescription, row }: DrugCostCardProps): ReactNode {
  const subline = [prescription.label, prescription.form].find((s) => s.trim() !== "");
  return (
    <section className="border-haze flex flex-col gap-5 rounded-2xl border bg-white p-6">
      <div className="flex flex-col gap-1">
        <h3 className="text-body-lg text-midnight font-bold">{prescription.drugName}</h3>
        {subline !== undefined && <p className="text-body-sm text-slate-teal">{subline}</p>}
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-body-sm text-slate-teal">Estimated cost</p>
        <p className="text-body-lg text-midnight font-bold">{drugCost(row)}</p>
      </div>
    </section>
  );
}

/**
 * "—" without an engine row, "Not covered by your plan" for an off-formulary
 * drug, else the one-fill range (which reads "—" for an unpriced row).
 */
function drugCost(row: M3pProjectionDrug | undefined): string {
  if (row === undefined) return "—";
  if (!row.covered) return "Not covered by your plan";
  return costRange(row.costMin, row.cost).text;
}
